import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { events, getEventSortDate, formatEventDisplayDate } from '../events/Events';
import {
  getEventStatus,
  setEventStatus,
  fetchMembersText,
  saveMembersText,
  getStoredAdminToken,
  setStoredAdminToken,
  validateAdminToken,
} from '../../services/adminService';
import {
  summarizeReservation,
  cleanMemberList,
  summarizeMemberList,
} from '../../utils/reservationStatus';
import './Admin.css';

// Bare numbers coming back from the API / typed into the inputs, normalized for
// comparison so we can tell whether a row has unsaved edits.
const fingerprint = (s) => `${s?.capacity ?? ''}|${s?.sold ?? ''}`;

function Admin() {
  const [statuses, setStatuses] = useState({});   // edited values
  const [savedStatuses, setSavedStatuses] = useState({}); // last known server values
  const [error, setError] = useState(null);
  const [savingSlug, setSavingSlug] = useState(null);
  const [justSaved, setJustSaved] = useState(null);
  const [membersText, setMembersText] = useState('');
  const [savedMembersText, setSavedMembersText] = useState('');
  const [savingMembers, setSavingMembers] = useState(false);
  const [membersSaved, setMembersSaved] = useState(false);
  // Null = loaded fine. A string = the list could not be read, so saving would
  // overwrite the stored list with whatever happens to be in the box.
  const [membersLoadError, setMembersLoadError] = useState(null);
  const [adminToken, setAdminToken] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showPast, setShowPast] = useState(false);

  // Event records joined to their reservation slug, newest first.
  const eventRows = useMemo(() => {
    const now = new Date();
    return events
      .map((e) => ({
        slug: e.rsvpLink.replace('/rsvp/', ''),
        title: e.title,
        presenter: e.presenter,
        dateLabel: formatEventDisplayDate(e),
        sortDate: getEventSortDate(e),
        isUpcoming: getEventSortDate(e) >= now,
        isClosed: !!e.isClosed,
      }))
      .sort((a, b) => b.sortDate - a.sortDate);
  }, []);

  const upcoming = eventRows.filter((r) => r.isUpcoming);
  const past = eventRows.filter((r) => !r.isUpcoming);

  const reload = useCallback(async () => {
    try {
      const map = {};
      for (const row of eventRows) {
        // eslint-disable-next-line no-await-in-loop
        const s = await getEventStatus(row.slug).catch(() => null);
        map[row.slug] = s || { slug: row.slug, capacity: null, sold: 0, remaining: null };
      }
      setStatuses(map);
      setSavedStatuses(map);
    } catch (err) {
      setError('Could not load reservation numbers. Check your connection and try Refresh.');
    }
  }, [eventRows]);

  const connect = useCallback(async (token) => {
    setError(null);
    setStoredAdminToken(token);
    if (!token) {
      setTokenValid(false);
      return;
    }
    setValidating(true);
    const ok = await validateAdminToken(token);
    setValidating(false);
    setTokenValid(ok);
    if (!ok) {
      setError('That password was not accepted. Check it and try again.');
      return;
    }
    await reload();
    try {
      const list = await fetchMembersText();
      setMembersText(list || '');
      setSavedMembersText(list || '');
      setMembersLoadError(null);
    } catch (_) {
      // Leave the box empty but remember that we never actually read the list,
      // so a Save can't silently replace it with nothing.
      setMembersText('');
      setSavedMembersText('');
      setMembersLoadError('The member list could not be loaded.');
    }
  }, [reload]);

  useEffect(() => {
    const stored = getStoredAdminToken();
    setAdminToken(stored);
    if (stored) connect(stored);
  }, [connect]);

  const signOut = () => {
    setStoredAdminToken('');
    setAdminToken('');
    setTokenValid(false);
    setStatuses({});
    setSavedStatuses({});
    setMembersText('');
    setSavedMembersText('');
    setMembersLoadError(null);
    setError(null);
  };

  const handleChange = (slug, field, value) => {
    setStatuses((prev) => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
  };

  const saveRow = async (slug, override) => {
    try {
      setSavingSlug(slug);
      setError(null);
      const next = override || statuses[slug] || {};
      const result = await setEventStatus({
        slug,
        capacity: Number(next.capacity),
        sold: Number(next.sold),
      });
      const merged = {
        ...statuses[slug],
        ...result,
        remaining: result.capacity != null
          ? Math.max(0, result.capacity - (result.sold ?? Number(next.sold) ?? 0))
          : null,
      };
      setStatuses((prev) => ({ ...prev, [slug]: merged }));
      setSavedStatuses((prev) => ({ ...prev, [slug]: merged }));
      setJustSaved(slug);
      setTimeout(() => setJustSaved((c) => (c === slug ? null : c)), 4000);
    } catch (err) {
      setError(err.message || 'Could not save. Your changes were not stored.');
    } finally {
      setSavingSlug(null);
    }
  };

  // Quick fix for the most common request: an event is full and needs to reopen.
  const addSeats = (slug, n) => {
    const current = statuses[slug] || {};
    const base = Number(current.capacity) || Number(current.sold) || 0;
    const next = { ...current, capacity: base + n };
    setStatuses((prev) => ({ ...prev, [slug]: next }));
    saveRow(slug, next);
  };

  const renderRow = (row) => {
    const s = statuses[row.slug] || {};
    const summary = summarizeReservation(s);
    const dirty = fingerprint(s) !== fingerprint(savedStatuses[row.slug]);
    const invalid = Number(s.capacity) < 0 || Number(s.sold) < 0;

    return (
      <div className={`res-row ${row.isUpcoming ? 'is-upcoming' : 'is-past'}`} key={row.slug}>
        <div className="res-row-head">
          <div>
            <div className="res-title">{row.title}</div>
            <div className="res-sub">
              {row.dateLabel} · {row.presenter}
            </div>
          </div>
          <span className={`res-badge tone-${row.isUpcoming ? summary.tone : 'past'}`}>
            {row.isUpcoming ? summary.label : 'Past event'}
          </span>
        </div>

        <div className="res-fields">
          <div className="res-field">
            <label htmlFor={`cap-${row.slug}`}>Seat limit</label>
            <input
              id={`cap-${row.slug}`}
              type="number"
              min="0"
              value={s.capacity ?? ''}
              placeholder="no limit"
              onChange={(e) => handleChange(row.slug, 'capacity', e.target.value)}
            />
          </div>
          <div className="res-field">
            <label htmlFor={`sold-${row.slug}`}>Seats reserved</label>
            <input
              id={`sold-${row.slug}`}
              type="number"
              min="0"
              value={s.sold ?? 0}
              onChange={(e) => handleChange(row.slug, 'sold', e.target.value)}
            />
          </div>
          <div className="res-actions">
            <button
              className="button"
              onClick={() => saveRow(row.slug)}
              disabled={savingSlug === row.slug || !dirty || invalid}
            >
              {savingSlug === row.slug ? 'Saving…' : 'Save'}
            </button>
            {justSaved === row.slug && <span className="res-saved">Saved ✓</span>}
            {dirty && justSaved !== row.slug && <span className="res-dirty">Unsaved changes</span>}
          </div>
        </div>

        {invalid && (
          <p className="res-note tone-bad">Numbers can't be negative.</p>
        )}

        {summary.over && (
          <p className="res-note tone-warn">
            More seats are reserved ({s.sold}) than the limit allows ({s.capacity}).
          </p>
        )}

        {row.isUpcoming && summary.state === 'full' && (
          <div className="res-note tone-bad">
            <strong>Members cannot reserve right now.</strong> The website shows “Event Full”.
            To reopen it, raise the seat limit above {Number(s.sold) || 0}.
            <button
              className="button button-inline"
              onClick={() => addSeats(row.slug, 10)}
              disabled={savingSlug === row.slug}
            >
              Add 10 seats
            </button>
          </div>
        )}

        {row.isUpcoming && summary.state === 'unlimited' && (
          <p className="res-note">
            No limit set, so reservations will never close on their own. Set a seat limit if the
            room has a fixed size.
          </p>
        )}
      </div>
    );
  };

  const memberSummary = summarizeMemberList(membersText);
  const storedMemberSummary = summarizeMemberList(savedMembersText);
  const membersDirty = membersText !== savedMembersText;
  // The list read back empty. That is either a genuinely empty list or a failed
  // read — indistinguishable from here, so warn before anything overwrites it.
  const membersLookEmpty = !membersLoadError && storedMemberSummary.count === 0;

  const downloadMemberList = () => {
    const blob = new Blob([membersText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hchs-members-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveMembers = async () => {
    if (membersLoadError) return;
    const removed = storedMemberSummary.count - memberSummary.count;
    if (removed > 0) {
      const ok = window.confirm(
        `This will save ${memberSummary.count} address${memberSummary.count === 1 ? '' : 'es'} and ` +
        `remove ${removed} that ${removed === 1 ? 'is' : 'are'} currently stored.\n\n` +
        `Removed addresses cannot be recovered. Continue?`
      );
      if (!ok) return;
    } else if (membersLookEmpty && memberSummary.count > 0) {
      const ok = window.confirm(
        'The stored member list looked empty when this page loaded, which can also mean it ' +
        'failed to load.\n\nSaving now replaces whatever is stored with these ' +
        `${memberSummary.count} address${memberSummary.count === 1 ? '' : 'es'}. Continue?`
      );
      if (!ok) return;
    }

    setSavingMembers(true);
    setError(null);
    try {
      await saveMembersText(membersText);
      setSavedMembersText(membersText);
      setMembersSaved(true);
      setTimeout(() => setMembersSaved(false), 4000);
    } catch (e) {
      setError(e.message || 'Could not save the member list.');
    } finally {
      setSavingMembers(false);
    }
  };

  return (
    <div className="admin-page">
      <section className="page-hero">
        <div className="feature-card admin-signin">
          <h3>Harris County Historical Society — Website Admin</h3>
          {tokenValid ? (
            <>
              <p className="meta">
                You're signed in. Below you can set how many seats each dinner has, see how many are
                reserved, and update the member email list.
              </p>
              <div className="admin-actions">
                <span className="res-badge tone-ok">Signed in</span>
                <button className="button secondary" onClick={reload} disabled={validating}>
                  Refresh numbers
                </button>
                <button className="button secondary" onClick={signOut}>
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="meta">
                Enter the admin password to manage dinner reservations and the member email list.
                If you don't have it, ask whoever manages the website.
              </p>
              <div className="form-group admin-password">
                <label htmlFor="admin-password">Admin password</label>
                <input
                  id="admin-password"
                  type="password"
                  value={adminToken}
                  autoComplete="current-password"
                  onChange={(e) => { setAdminToken(e.target.value); setTokenValid(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && adminToken) connect(adminToken); }}
                  placeholder="Admin password"
                />
              </div>
              <div className="admin-actions">
                <button
                  className="button"
                  onClick={() => connect(adminToken)}
                  disabled={!adminToken || validating}
                >
                  {validating ? 'Checking…' : 'Sign in'}
                </button>
                <span className="meta">Your browser will remember this on this computer.</span>
              </div>
            </>
          )}
        </div>
      </section>

      <div className="page-container">
        {error && <div className="error-message">{error}</div>}

        {!tokenValid ? (
          <div className="feature-card">
            <h3>Sign in to continue</h3>
            <p className="meta">Enter the admin password above to load the controls.</p>
          </div>
        ) : (
          <>
            <div className="feature-card admin-section">
              <h3>Dinner reservations</h3>
              <p className="meta">
                <strong>Seat limit</strong> is how many people the dinner can hold.{' '}
                <strong>Seats reserved</strong> counts <em>people</em>, not reservations — a member
                bringing two guests takes three seats. When reserved reaches the limit, the website
                shows “Event Full” and stops taking reservations.
              </p>

              {upcoming.length > 0 ? (
                upcoming.map(renderRow)
              ) : (
                <div className="res-note tone-warn">
                  <strong>There are no upcoming events.</strong> Members have nothing to reserve
                  until the next dinner is added to the website. Adding a program is a website
                  change rather than something you can do on this page — see{' '}
                  <code>docs/EDITING.md</code> or ask whoever maintains the site.
                </div>
              )}

              <div className="admin-actions past-toggle">
                <button className="button secondary" onClick={() => setShowPast((v) => !v)}>
                  {showPast ? 'Hide past events' : `Show past events (${past.length})`}
                </button>
              </div>
              {showPast && (
                <div className="past-list">
                  <p className="meta">
                    These dinners have already happened. Their numbers are kept as a record —
                    there's usually no reason to change them.
                  </p>
                  {past.map(renderRow)}
                </div>
              )}
            </div>

            <div className="feature-card admin-section">
              <h3>Member email list</h3>
              <p className="meta">
                One email address per line. This list is what gives members the member price when
                they reserve, so keep it current.
              </p>
              {membersLoadError && (
                <div className="res-note tone-bad">
                  <strong>{membersLoadError}</strong> Saving is turned off so the stored list can't
                  be replaced by an empty box. Click <strong>Refresh numbers</strong> above, or
                  reload the page, and check the addresses appear before editing.
                </div>
              )}
              {membersLookEmpty && (
                <div className="res-note tone-warn">
                  <strong>The stored list came back empty.</strong> If you expect addresses here,
                  don't save — reload the page first. Saving would replace the stored list.
                </div>
              )}
              <div className="members-stats">
                <span className="res-badge tone-ok">{memberSummary.count} addresses</span>
                {memberSummary.duplicates > 0 && (
                  <span className="res-badge tone-warn">
                    {memberSummary.duplicates} duplicate{memberSummary.duplicates === 1 ? '' : 's'}
                  </span>
                )}
                {memberSummary.unparsable > 0 && (
                  <span className="res-badge tone-warn">
                    {memberSummary.unparsable} line{memberSummary.unparsable === 1 ? '' : 's'} with
                    no email
                  </span>
                )}
                {membersDirty && <span className="res-dirty">Unsaved changes</span>}
              </div>
              <textarea
                aria-label="Member email addresses, one per line"
                value={membersText}
                onChange={(e) => { setMembersText(e.target.value); setMembersSaved(false); }}
              />
              <div className="admin-actions">
                <button
                  className="button"
                  disabled={savingMembers || !membersDirty || !!membersLoadError}
                  onClick={handleSaveMembers}
                >
                  {savingMembers ? 'Saving…' : 'Save member list'}
                </button>
                <button
                  className="button secondary"
                  onClick={() => setMembersText(cleanMemberList(membersText))}
                >
                  Tidy up list
                </button>
                <button
                  className="button secondary"
                  onClick={downloadMemberList}
                  disabled={memberSummary.count === 0}
                >
                  Download a copy
                </button>
                {membersSaved && <span className="res-saved">Saved ✓</span>}
                <span className="meta">
                  “Tidy up” strips names and extra text, leaving one address per line. Check the
                  result, then Save. Before a big edit, use “Download a copy” to keep a backup —
                  saving replaces the stored list and removed addresses can't be recovered.
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Admin;
